import { useNavigate } from 'react-router-dom';
import LimitedEditionIcon from "src/assets/all-icons/limited-edition-icon.jsx";

import useStateHandler from '@/hooks/useStateHandler';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import Utils from '@/utils';
import { getArtworkArtistDisplayName } from '@/utils/artworkArtists';

import ArtworkImage from './ArtworkImage';

import './styles/OrderCard.css';

export default function OrderCard({
  orderData,
  children,
  headerAction,
  navigateToView = false,
  onSeeIssue,
  showShippingReminder = true,
  showArtworkImage = true,
  variant = "list",
}) {
  const { state } = useStateHandler();
  const navigate = useNavigate();
  const order = orderData ?? {};
  const artwork = order.order_artwork ?? {};
  const artworkAbout = artwork.artwork_about ?? {};
  const artworkSize = artworkAbout.artwork_size ?? {};
  const artworkImageId = artwork?.artwork_media?.artwork_main_picture?.image_id ?? null;
  const hasArtworkImage = typeof artworkImageId === "string" && artworkImageId.length > 0;
  const currentUserId = state.user_session?._id ?? null;
  const orderBuyerId = order.order_buyer?._id ?? order.order_buyer ?? null;
  const isBuyer = Boolean(
    currentUserId &&
    orderBuyerId &&
    String(currentUserId) === String(orderBuyerId)
  );
  const paymentStatus = order.status ?? null;
  const shippingStatus = order.order_shipping_address?.shipping_status
    ?? order.order_status
    ?? paymentStatus
    ?? null;
  const hasShippingAddress = Boolean(
    order.order_shipping_address?.address_name &&
    order.order_shipping_address?.address_street &&
    order.order_shipping_address?.address_country
  );
  const shouldShowShippingAlert = Boolean(
    showShippingReminder &&
    isBuyer &&
    !hasShippingAddress &&
    (
      shippingStatus === "payment_pending" ||
      (paymentStatus === "paid" && order.type === "auction")
    )
  );
  const isClickable = Boolean(navigateToView);
  const unreadMessageCount = Math.max(0, Number(order.unreadMessageCount) || 0);
  const hasUnreadMessages = variant === "list" && unreadMessageCount > 0;
  const artistDisplayName = getArtworkArtistDisplayName(artwork);
  const artworkTitle = artworkAbout.artwork_title || "Artwork details unavailable";
  const artworkMedium = artworkAbout.artwork_medium || "Medium unavailable";
  const artworkDimensions = [
    artworkSize.length,
    artworkSize.unit,
    artworkSize.height,
    artworkSize.unit,
  ].every((value) => value !== null && value !== undefined && value !== "")
    ? `${artworkSize.length} ${artworkSize.unit} x ${artworkSize.height} ${artworkSize.unit}`
    : "Dimensions unavailable";
  const orderTotal = order.order_payment?.total;
  const hasOrderTotal = (typeof orderTotal === "number" && Number.isFinite(orderTotal))
    || (typeof orderTotal === "string" && orderTotal.length > 0);

  return (
    <>
      <div className={`order-card__container order-card__container--${variant}${hasUnreadMessages ? ' order-card__container--unread' : ''}`}>
        {/* The clickable area for navigation */}
        <div 
          className={`order-card__clickable ${isClickable ? "pointer" : ""}`} 
          onClick={
            isClickable ? 
              () => navigate(`/order/${order.order_number}`, {
                state: { from: true }
              }) 
              : 
              null
          }
        >
          <div className="order-card__header">
            { shippingStatus === "cancelled" ? (
              <span className="order-card__header-text canceled dots-on-overflow">
                  Order canceled
              </span>
            ) : shippingStatus === "payment_pending" ? (
              <span className="order-card__header-text dots-on-overflow">
                  Payment in progress
              </span>
            ) : isBuyer ? (
              order.order_limited_edition_data ? (
                <>
                  <LimitedEditionIcon className="order-card__icon"/>
                  <span className="order-card__header-text dots-on-overflow">
                      Suarte
                  </span>
                </>
              ) : (
                <>
                  <UserProfileImage
                    image={order.order_seller?.user_image?.image_id}
                    typeOfProfile={order.order_seller?.user_type}
                    className="order-card__icon"
                  />
                  <span 
                    className="order-card__header-text dots-on-overflow"
                    onClick={(e) => {
                      e.stopPropagation();
                      const sellerUsername = order.order_seller?.user_username;
                      if (sellerUsername) {
                        navigate(`/user/${sellerUsername}`, { state: { from: true } });
                      }
                    }}
                  >
                    {order.order_seller?.user_name ?? "Gallery"}
                  </span>
                </>
              )
            ) : order.order_issue ? (
              <span className="order-card__header-text theme dots-on-overflow">
                  An issue has been reported
              </span>
            ) : (
              <span className="order-card__header-text dots-on-overflow">
                { shippingStatus === "pending" ? "Confirmed" :
                  shippingStatus === "sent" ? "On its way" : "Completed" }
              </span>
            )
            }
            {headerAction && (
              <div className="order-card__header-action">
                {headerAction}
              </div>
            )}

            {hasUnreadMessages && (
              <span className="order-card__unread-count" aria-label={`${unreadMessageCount} new messages`}>
                {unreadMessageCount}
              </span>
            )}

            <span className="order-card__date">
              {Utils.getDateInStringFromTimestamp(new Date(order.createdAt).getTime())}
            </span>
          </div>

          <div className="order-card__content">
            {showArtworkImage && (hasArtworkImage ? (
              <ArtworkImage
                image={artworkImageId}
                imageClassName="order-card__image"
                imageTemplateClassName="order-card__image"
                forceSmaller={300}
              />
            ) : (
              <div
                className="order-card__image order-card__image--unavailable"
                aria-label="Artwork image unavailable"
                role="img"
              />
            )
            )}

            <div className="order-card__info">
              <div className="order-card__title-section">
                <span className="order-card__big-text dots-on-overflow --title">
                  {artworkTitle}
                  <span>
                    { order.order_limited_edition_data ? " - Limited Edition" : " - Original" }
                  </span>
                </span>

                <div className="order-card__title-meta">
                  {shouldShowShippingAlert && (
                    <span className="order-card__shipping-alert">
                      Shipping address required
                    </span>
                  )}

                  <span className="order-card__date hidden">
                    {Utils.getDateInStringFromTimestamp(new Date(order.createdAt).getTime())}
                  </span>
                </div>
              </div>

              <div className="order-card__details">
                {artistDisplayName !== "-" && (
                  <span className="order-card__small-text dots-on-overflow">
                    by {artistDisplayName}
                  </span>
                )}

                <span className="order-card__small-text dots-on-overflow">
                  {artworkMedium}
                </span>

                <span className="order-card__small-text dots-on-overflow">
                  {artworkDimensions}
                </span>
              </div>

              <span className="order-card__big-text --total">
                Total:{" "}
                {hasOrderTotal
                  ? Utils.getArtworkPrice(orderTotal, order.order_payment?.currency)
                  : "Unavailable"}
              </span>
            </div>
          </div>
        </div>

        {/* Conditionally render the "See Issue" button if an issue has been reported */}
        {order.order_issue && onSeeIssue && (
          <div 
            className="order-card__see-issue-button" 
            onClick={(e) => { 
              e.stopPropagation(); 
              onSeeIssue(); 
            }}
          >
            <ContinueButton>
              See issue
            </ContinueButton>
          </div>
        )}

        {children && (
          <div className="order-card__actions">
            {children}
          </div>
        )}
      </div>
    </>
  );
}
